import { useState, useEffect, useRef, lazy, Suspense, useCallback } from "react";
import { ArrowRight, Play, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import heroBg from "@/assets/u3714841198_Sleek_minimalistic_abstract_visual_inspired_by_he_53bffa3d-7c36-4acb-858b-afa6bbf35d2a_0.png?format=webp&quality=80";
import { HeroScrollDown } from "./HeroScrollDown";

// Lazy load the heavy ProjectSimulator modal (only loaded when opened)
const ProjectSimulator = lazy(() => import("./ProjectSimulator"));

export const Hero = () => {
  const { t } = useTranslation();
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const heroRef = useRef<HTMLElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const rafId = useRef(0);
  const ticking = useRef(false);
  const lastY = useRef(0);

  // Listen for global event to open simulator (from chatbot)
  useEffect(() => {
    const handleOpenSimulator = () => setSimulatorOpen(true);
    window.addEventListener("open-simulator", handleOpenSimulator);
    return () => window.removeEventListener("open-simulator", handleOpenSimulator);
  }, []);

  // Parallax scroll — compositor-only transform, no scale, no repaint
  const applyParallax = useCallback(() => {
    if (imgRef.current) {
      const y = lastY.current;
      // Single translate3d only — stays on GPU compositor thread
      imgRef.current.style.transform = `translate3d(0, ${Math.round(y * 0.25)}px, 0)`;
    }
    ticking.current = false;
  }, []);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const handleScroll = () => {
      lastY.current = window.scrollY;

      if (!ticking.current && heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        if (rect.bottom > 0) {
          ticking.current = true;
          rafId.current = requestAnimationFrame(applyParallax);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafId.current);
    };
  }, [applyParallax]);

  return (
    <>
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">

        {/* ===== Parallax image — isolated on its own GPU layer ===== */}
        <div
          className="absolute inset-0 z-0"
          style={{ contain: "strict", isolation: "isolate" }}
        >
          <img
            ref={imgRef}
            src={heroBg}
            alt=""
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="async"
            className="w-full h-[130%] object-cover"
            style={{
              willChange: "transform",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "translate3d(0, 0, 0)",
              containIntrinsicSize: "1920px 1080px",
            }}
          />
        </div>

        {/* ===== Static overlays — separate layer, never repainted by parallax ===== */}
        <div className="absolute inset-0 z-[1] pointer-events-none">
          {/* Filters applied here — static, painted once */}
          <div className="absolute inset-0 brightness-[1.1] contrast-[1.15] saturate-[1.05] mix-blend-normal" />

          {/* Readability overlay */}
          <div className="absolute inset-0 bg-background/35" />

          {/* Subtle control-room gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-background/10 via-transparent to-background/10" />
        </div>

        {/* ===== Content ===== */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24">
          <div className="max-w-4xl mx-auto space-y-8">

            {/* ===== Badge ===== */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-badge-bg border border-border animate-fade-in scale-95 sm:scale-100">
              <span className="w-2 h-2 rounded-full bg-badge-dot" />
              <span className="text-sm font-medium text-muted-foreground">
                {t("hero.badge")}
              </span>
            </div>

            {/* ===== Headline ===== */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight animate-fade-in [animation-delay:120ms] opacity-0">
              {t("hero.title")}
              <span className="block text-foreground/70 mt-1 sm:mt-0">
                {t("hero.titleHighlight")}
              </span>
            </h1>

            {/* ===== Subline ===== */}
            {/* Desktop */}
            <p className="mt-6 text-lg sm:text-xl text-foreground/65 max-w-3xl mx-auto animate-fade-in opacity-0 [animation-delay:240ms] hidden sm:block">
              {t("hero.subtitle")}
            </p>

            {/* Mobile (short, higher contrast) */}
              <p className="mt-6 text-base text-foreground/80 max-w-xl mx-auto animate-fade-in opacity-0 [animation-delay:240ms] sm:hidden">
              {t("hero.subtitleMobile")}
             </p>

            {/* ===== Supporting line (desktop only) ===== */}
            <p className="text-base sm:text-lg text-foreground/55 max-w-2xl mx-auto animate-fade-in opacity-0 [animation-delay:300ms] hidden sm:block">
              {t("hero.subtitle2")}
            </p>

            {/* ===== CTA Buttons ===== */}
            <div className="pt-10 sm:pt-4 flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in [animation-delay:340ms] opacity-0">
                <Button
                size="lg"
                className="bg-primary text-primary-foreground font-medium px-6 py-3 rounded-md transition-colors hover:bg-accent hover:text-accent-foreground"
                asChild
              >
                <a href="#services" className="inline-flex items-center">
                  {t("hero.cta")}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </Button>

              <Button
                size="lg"
                variant="outline"
                onClick={() => setSimulatorOpen(true)}
                className="
                  border-foreground/30
                  text-foreground
                  font-medium
                  px-6
                  py-3
                  rounded-md
                  transition-all
                  hover:bg-foreground/10
                  hover:border-foreground/50
                "
              >
                <Play className="mr-2 h-4 w-4" />
                {t("hero.simulatorCta")}
              </Button>
            </div>



            {/* ===== Compliance Badges ===== */}
            <div className="pt-10 flex flex-wrap items-center justify-center gap-3 animate-fade-in [animation-delay:420ms] opacity-0">
              {(["ISO 27001", "NIS2", "GDPR", "SOC 2"] as const).map((badge) => (
                <span
                  key={badge}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-foreground/15 bg-background/40 backdrop-blur-sm text-[11px] font-medium tracking-wider text-foreground/60 uppercase select-none"
                >
                  <ShieldCheck className="w-3 h-3 text-accent shrink-0" />
                  {badge}
                </span>
              ))}
            </div>

          </div>
        </div>

        {/* Scroll Down Arrow - inside Hero for proper absolute positioning */}
        <HeroScrollDown />
      </section>

      {/* Project Simulator Modal - lazy loaded, always mounted once opened to preserve modal state */}
      <Suspense fallback={null}>
        <ProjectSimulator
          isOpen={simulatorOpen}
          onClose={() => setSimulatorOpen(false)}
        />
      </Suspense>
    </>
  );
};